import type { SvgIconComponent } from "@mui/icons-material";
import SettingsTwoToneIcon from "@mui/icons-material/SettingsTwoTone";
import VpnKeyTwoToneIcon from "@mui/icons-material/VpnKeyTwoTone";
import PaymentsTwoToneIcon from "@mui/icons-material/PaymentsTwoTone";
import BuildTwoToneIcon from "@mui/icons-material/BuildTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import BadgeTwoToneIcon from "@mui/icons-material/BadgeTwoTone";
import type { RolePermissions } from "../components/PermissionsContext";

export type RoleTemplate = {
  key: "Owner" | "Admin" | "BillingManager" | "Operator" | "Viewer";
  label: string;
  icon: SvgIconComponent;
  permissions: RolePermissions;
};

/**
 * Fallback icon for custom roles (any role whose name doesn't match a template).
 * Exported so RoleCard / IAM page can use the same default.
 */
export const DEFAULT_ROLE_ICON: SvgIconComponent = BadgeTwoToneIcon;

const empty: RolePermissions = {
  orgInfos: [],
  orgCollaborators: [],
  orgRoles: [],
  orgPayments: [],
  invoices: [],
  dashboard: [],
  support: [],
  media: [],
  auditLog: [],
  contact: [],
  hall: [],
  roomRadioAds: [],
  roomTvAds: [],
  roomChatAds: [],
  hallAds: [],
  bid: [],
  handleNegotiateConfigs: [],
  report: [],
};

const allCrud = ["view", "create", "edit", "delete"];
const allCrudPlusBudget = [...allCrud, "setBudget"];
const allBid = ["view", "create", "viewRanking", "viewHistory"];

export const ROLE_TEMPLATES: RoleTemplate[] = [
  {
    key: "Owner",
    label: "Proprietário",
    icon: SettingsTwoToneIcon,
    permissions: {
      ...empty,
      orgInfos: ["all"],
      orgCollaborators: ["all"],
      orgRoles: ["all"],
      orgPayments: ["all"],
      invoices: ["all"],
      dashboard: ["all"],
      support: ["all"],
      media: ["all"],
      roomRadioAds: ["all"],
      roomTvAds: ["all"],
      hallAds: ["all"],
      bid: ["all"],
      handleNegotiateConfigs: ["all"],
      report: ["all"],
    },
  },
  {
    key: "Admin",
    label: "Administrador",
    icon: VpnKeyTwoToneIcon,
    permissions: {
      ...empty,
      orgInfos: allCrud,
      orgCollaborators: allCrud,
      orgRoles: allCrud,
      orgPayments: allCrud,
      invoices: ["view", "export"],
      dashboard: ["view"],
      support: ["view", "create"],
      media: ["view", "upload"],
      roomRadioAds: allCrudPlusBudget,
      roomTvAds: allCrudPlusBudget,
      hallAds: allCrud,
      bid: allBid,
      handleNegotiateConfigs: ["view", "edit"],
      report: ["view", "create"],
    },
  },
  {
    key: "BillingManager",
    label: "Gestor de Cobrança",
    icon: PaymentsTwoToneIcon,
    permissions: {
      ...empty,
      orgPayments: allCrud,
      invoices: ["view", "export"],
      dashboard: ["view"],
    },
  },
  {
    key: "Operator",
    label: "Operador",
    icon: BuildTwoToneIcon,
    permissions: {
      ...empty,
      roomRadioAds: allCrud,
      roomTvAds: allCrud,
      hallAds: allCrud,
      media: ["view", "upload"],
      bid: ["view", "create"],
      report: ["view", "create"],
    },
  },
  {
    key: "Viewer",
    label: "Leitor",
    icon: VisibilityTwoToneIcon,
    permissions: {
      ...empty,
      orgInfos: ["view"],
      orgCollaborators: ["view"],
      orgRoles: ["view"],
      orgPayments: ["view"],
      invoices: ["view"],
      dashboard: ["view"],
      support: ["view"],
      media: ["view"],
      roomRadioAds: ["view"],
      roomTvAds: ["view"],
      hallAds: ["view"],
      bid: ["view", "viewRanking", "viewHistory"],
      handleNegotiateConfigs: ["view"],
      report: ["view"],
    },
  },
];
